import {
  AccountSummary,
  AccountSummaryState,
  INFO_ACCOUNT_SUMMARY,
  UPDATE_PROMOTIONS,
} from "./AccountInterface";
import { ActionContext } from "vuex";
import * as fb from "../../firebase";
import dayjs from "dayjs";
import { UnilevelMember } from "../Unilevel/UnilevelInterfaces";
import { NetworkDataScholarship } from "functions/src/Users/UsersInterfaces";
import { collection, doc, getDoc, Timestamp } from "firebase/firestore";
import {
  ACADEMY_LICENSE_IDS,
  ACADEMY_SUBSCRIPTION_IDS,
  noVolumeAcademySubscriptionId,
} from "functions/src/Subscriptions/packageIds";

/**
 * LA CLAVE BAJO LA QUE VIVE EL SALDO DE ACTIVACIONES REGALADAS.
 *
 * El libro del patrocinador (`users/{u}/userIncentives/promotions`) está
 * indexado por DOC-ID de suscripción, no por etiqueta: cada clave dice de qué
 * paquete concreto le quedan activaciones por regalar. La herramienta «Activar
 * SIN Volumen» entra SIEMPRE con `both` —los tres sitios del front que la
 * llaman mandan esa cadena—, y para `both` la puerta del backend
 * (`noVolumeAcademyId`, en `Activations/GTRAcademy.ts`) resuelve el id NUEVO.
 * Se calcula aquí con la misma función que usa el pre-vuelo del admin para que
 * las dos mitades no puedan separarse: si mañana cambia el paquete que se
 * regala, cambia en un sitio y esta pantalla va detrás.
 */
const CLAVE_PROMO_ACADEMIA = noVolumeAcademySubscriptionId("both");

const initialState: AccountSummaryState = {
  account: {
    academyDaysLeft: 0,
    distributorDaysLeft: 0,
    directAffiliates: 0,
    hasBinaryNetworkBonus: false,
    bonusRemainingDays: 0,
    configuredWallet: false,
    binaryBonusExpiringSide: undefined,
    binarySide: undefined,
    binaryPercentage: 0,
    binaryExpiration: {
      left: {
        endDate: undefined,
        daysLeft: 0,
      },
      right: {
        endDate: undefined,
        daysLeft: 0,
      },
    },
    promotions: {
      tradingAcademy: 0,
      distributorLicense: 0,
      both: 0,
    },
    innerLegUnlocked: undefined,
    scholarshipPoints: 0,
    scholarshipStartDate: undefined,
    scholarshipEndDate: undefined,
    unilevelData: new UnilevelMember(
      "",
      "",
      "",
      "",
      "",
      new Map<string, any>()
    ),
  },
};

const state: AccountSummaryState = { ...initialState };

const mutations = {
  INFO_ACCOUNT_SUMMARY(state: AccountSummaryState, summary: AccountSummary) {
    if (state == undefined) {
      state = { ...initialState };
    }
    state.account = summary;
  },
  UPDATE_PROMOTIONS(state: AccountSummaryState, promotions: any) {
    if (state.account.promotions) {
      state.account.promotions = promotions;
    }
  },
  /**
   * Recibe `state` A PROPÓSITO. Sin el parámetro escribía en el objeto crudo
   * del módulo y no en el Proxy reactivo de Vuex, así que el borrado no
   * invalidaba `getAccountSummary`: el resumen de cuenta del usuario anterior
   * (lado del binario, pierna interna, días de licencia) seguía sirviéndose
   * cacheado en la sesión siguiente. Mismo defecto que había en
   * `UnilevelStore`, explicado con detalle allí.
   */
  CLEAR_STATE(state: AccountSummaryState) {
    Object.assign(state, initialState);
  },
};

const actions = {
  async initializeAccountSummary(
    context: ActionContext<AccountSummaryState, any>,
    username: string
  ) {
    const accountResponse: AccountSummary = {
      academyDaysLeft: 0,
      distributorDaysLeft: 0,
      directAffiliates: 0,
      hasBinaryNetworkBonus: false,
      bonusRemainingDays: 0,
      configuredWallet: false,
      binaryBonusExpiringSide: undefined,
      binarySide: undefined,
      binaryPercentage: 0,
      binaryExpiration: {
        left: {
          endDate: undefined,
          daysLeft: 0,
        },
        right: {
          endDate: undefined,
          daysLeft: 0,
        },
      },
      promotions: {
        tradingAcademy: 0,
        distributorLicense: 0,
        both: 0,
      },
      innerLegUnlocked: undefined,
      scholarshipPoints: 0,
      scholarshipStartDate: undefined,
      scholarshipEndDate: undefined,
      unilevelData: new UnilevelMember(
        "",
        "",
        "",
        "",
        "",
        new Map<string, any>()
      ),
    };
    const _usernamePK = username.toLowerCase();
    const _user = doc(fb.firestore, "users", _usernamePK);
    // LA LICENCIA, EN LAS DOS QUE VALEN: la de siempre o la GLOBAL. Se leen las
    // dos y manda la de `endDate` mayor — con sólo la vieja presente el
    // resultado es exactamente el de antes.
    const _licenseDocs = ACADEMY_LICENSE_IDS.map((id) =>
      doc(fb.firestore, "users", _usernamePK, "subscriptions", id)
    );
    // LA ACADEMIA, EN LAS DOS GENERACIONES, exactamente como la licencia de
    // arriba: un traducido tiene `trading-academy` VENCIDO y `gtr-fly` VIGENTE, y
    // leyendo sólo el viejo esta pantalla toma la fecha muerta por la buena.
    const _academyDocs = ACADEMY_SUBSCRIPTION_IDS.map((id) =>
      doc(fb.firestore, "users", _usernamePK, "subscriptions", id)
    );
    const _userIncentives = collection(
      fb.firestore,
      "users",
      _usernamePK,
      "userIncentives"
    );
    // Network reset (Fase 4): directsCount lives on the unilevelTree node and
    // the binary bonus fields (binaryExpiration/innerLegUnlocked/binarySide/
    // binaryPercentage) on the binaryTree node — networkData/basic and
    // networkData/directAffiliates dissolved (§5). scholarship and promotions
    // moved out of networkData into the new userIncentives collection (§5, #558).
    const _directAffiliates = doc(fb.firestore, "unilevelTree", _usernamePK);
    const _networkBasic = doc(fb.firestore, "binaryTree", _usernamePK);
    const _networkScholarship = doc(_userIncentives, "scholarship");
    const _financial = doc(
      fb.firestore,
      "users",
      _usernamePK,
      "profile",
      "financial"
    );
    const today = new Date().valueOf();

    const distributorPromise = Promise.all(_licenseDocs.map((d) => getDoc(d)))
      .then((responses) => {
        // La que caduca más tarde MANDA, igual que en el resto del código.
        const subsInfo = responses
          .map((r) => r.data())
          .filter((d) => d != undefined)
          .sort(
            (a, b) =>
              (b?.endDate?.toMillis?.() ?? -Infinity) -
              (a?.endDate?.toMillis?.() ?? -Infinity)
          )[0];
        if (
          subsInfo?.endDate != undefined &&
          subsInfo?.startDate != undefined
        ) {
          // ⚠️ `subsInfo.left` NO EXISTE en un documento de suscripción, así que
          // esta rama SIEMPRE cae en el `: 0`. Es un fallo PREEXISTENTE (el
          // `left`/`right` es del nodo binario, no de la suscripción) y se deja
          // EXACTAMENTE como estaba: arreglarlo cambiaría un número visible para
          // todo el mundo, y eso no es lo que se pidió aquí. Queda anotado.
          accountResponse.distributorDaysLeft = subsInfo.left
            ? Math.floor(
                dayjs(subsInfo.left.endDate.toDate()).diff(today, "day", true)
              )
            : 0;
        } else {
          accountResponse.distributorDaysLeft = 0;
        }
        return Promise.resolve();
      })
      .catch((error) => {
        return Promise.reject(error);
      });

    const academyPromise = Promise.all(_academyDocs.map((d) => getDoc(d)))
      .then((responses) => {
        // La que caduca más tarde MANDA, igual que la licencia.
        const subsInfo = responses
          .map((r) => r.data())
          .filter((d) => d != undefined)
          .sort(
            (a, b) =>
              (b?.endDate?.toMillis?.() ?? -Infinity) -
              (a?.endDate?.toMillis?.() ?? -Infinity)
          )[0];
        if (
          subsInfo?.endDate != undefined &&
          subsInfo?.startDate != undefined
        ) {
          accountResponse.academyDaysLeft = subsInfo.left
            ? Math.floor(
                dayjs(subsInfo.left.endDate.toDate()).diff(today, "day", true)
              )
            : 0;
          accountResponse.scholarshipStartDate = subsInfo.endDate.toDate();
        } else {
          accountResponse.academyDaysLeft = 0;
        }
        return Promise.resolve();
      })
      .catch((error) => {
        return Promise.reject(error);
      });

    const bonusPromise = await Promise.all([distributorPromise, academyPromise])
      .then(async () => {
        // `binarySide` is written at REGISTRATION on the users doc and mirrored
        // onto the binary node at placement. Read it here so an unplaced member
        // (no node) still has one — the power-line lock below needs it to know
        // which leg to force.
        const userDocData = await getDoc(_user).then((snap) => snap.data());

        return getDoc(_networkBasic)
          .then((response) => {
            const basicInfo = response.data();
            const expirationDts = basicInfo?.binaryExpiration;

            // NOT PLACED => no binaryTree node => these come back undefined, and
            // every consumer of `innerLegUnlocked` defaults undefined to TRUE
            // (unlocked). Before the cutover that never happened: registerUser
            // seeded networkData/basic AT SIGN-UP with
            // `{ binaryPercentage: 10, innerLegUnlocked: false }`. Placement now
            // happens at the first ACTIVATION (§17), so the field simply is not
            // there yet — and the power-line lock silently opened for every
            // registered-not-yet-activated member. Nobody decided that; it fell
            // out of the field's absence.
            //
            // Reinstate exactly what registration used to write. (placeMember
            // writes the same two values, so a placed member is unaffected.)
            accountResponse.innerLegUnlocked =
              basicInfo?.innerLegUnlocked ?? false;
            accountResponse.binaryPercentage =
              basicInfo?.binaryPercentage ?? 10;
            // `binarySide` never moved — it still lives on the users doc, which
            // registration always writes. Fall back to it rather than show "".
            accountResponse.binarySide =
              basicInfo?.binarySide ?? userDocData?.binarySide;

            if (expirationDts != undefined) {
              const leftDays = expirationDts.left
                ? Math.floor(
                    dayjs(expirationDts.left.endDate.toDate()).diff(
                      today,
                      "day",
                      true
                    )
                  )
                : 0;
              const rightDays = expirationDts.right
                ? Math.floor(
                    dayjs(expirationDts.right.endDate.toDate()).diff(
                      today,
                      "day",
                      true
                    )
                  )
                : 0;

              // If both are positive, then the bonus is active
              if (leftDays >= 0 && rightDays >= 0) {
                if (leftDays >= rightDays) {
                  accountResponse.hasBinaryNetworkBonus = true;
                  accountResponse.bonusRemainingDays = rightDays;
                  accountResponse.binaryBonusExpiringSide = "right";
                } else {
                  accountResponse.hasBinaryNetworkBonus = true;
                  accountResponse.bonusRemainingDays = leftDays;
                  accountResponse.binaryBonusExpiringSide = "left";
                }
              }
              accountResponse.binaryExpiration.left.endDate =
                expirationDts.left?.endDate.toDate() ?? undefined;
              accountResponse.binaryExpiration.right.endDate =
                expirationDts.right?.endDate.toDate() ?? undefined;

              accountResponse.binaryExpiration.left.daysLeft = leftDays;
              accountResponse.binaryExpiration.right.daysLeft = rightDays;
              return Promise.resolve();
            } else {
              return Promise.resolve();
            }
          })
          .catch((error) => {
            return Promise.reject(error);
          });
      })
      .catch((error) => {
        return Promise.reject(error);
      });

    const affiliatesPromise = getDoc(_directAffiliates)
      .then((response) => {
        const affiliates = response.data();
        // NOT PLACED => no unilevelTree node => no `directsCount`. This used to
        // write `undefined` over the initial 0, and AccountSummary's
        // `v-if="accountSummary.directAffiliates"` then HID the directs row
        // instead of showing a zero. Registration used to pre-create the doc, so
        // the field was always there; it is not any more (§17).
        accountResponse.directAffiliates = affiliates?.directsCount ?? 0;
        return Promise.resolve();
      })
      .catch((error) => {
        return Promise.reject(error);
      });

    const getLeaderData = fb.func.httpsCallable("Unilevel-getLeaderData");
    const unilevelData = getLeaderData({
      username: _usernamePK,
    }).then((result) => {
      if (result.data.OPCODE == "SUCCESS") {
        const memberData = result.data.data;
        for (const rankData in memberData.ranksCarrerData) {
          if (memberData.ranksCarrerData[rankData].date) {
            const timestamp = new Timestamp(
              memberData.ranksCarrerData[rankData].date._seconds,
              memberData.ranksCarrerData[rankData].date._nanoseconds
            );
            memberData.ranksCarrerData[rankData].date = timestamp;
          }
        }
        memberData.subscriptions = new Map(
          Object.entries(memberData.subscriptions)
        );

        if (memberData.scholarshipData != undefined) {
          memberData.scholarshipData.periodEndDate = new Timestamp(
            memberData.scholarshipData.periodEndDate._seconds,
            memberData.scholarshipData.periodEndDate._nanoseconds
          ).toDate();
        }
        accountResponse.unilevelData = memberData as UnilevelMember;
        return Promise.resolve();
      } else {
        return Promise.reject(result.data);
      }
    });
    const financialPromise = getDoc(_financial)
      .then((response) => {
        const financial = response.data();
        if (financial?.btcWallet && financial?.btcWallet.address.trim() != "") {
          accountResponse.configuredWallet = true;
        } else {
          accountResponse.configuredWallet = false;
        }
        return Promise.resolve();
      })
      .catch((error) => {
        return Promise.reject(error);
      });

    const scholarshipPromise = getDoc(_networkScholarship)
      .then((response) => {
        let scholarship: NetworkDataScholarship =
          response.data() as NetworkDataScholarship;
        if (scholarship) {
          if (
            (scholarship.periodEndDate as Timestamp).toDate() < new Date() &&
            scholarship.nextPeriods.length > 0
          ) {
            accountResponse.scholarshipPoints = -100;
            // Update scholarship data
            const UpdateUserScholarshipPeriod = fb.func.httpsCallable(
              "Subscriptions-UpdateUserScholarshipPeriod"
            );
            UpdateUserScholarshipPeriod({
              username: _usernamePK,
            })
              .then(async (result) => {
                if (result.data.OPCODE == "SUCCESS") {
                  // Get new scholarship data
                  scholarship = await getDoc(_networkScholarship)
                    .then((response) => {
                      return response.data() as NetworkDataScholarship;
                    })
                    .catch((error) => {
                      return Promise.reject(error);
                    });
                } else {
                  // console.log(result.data);
                }
              })
              .catch((error) => {
                console.error(error);
              });
          }
          accountResponse.scholarshipPoints = scholarship.periodPoints;
          accountResponse.scholarshipEndDate = (
            scholarship.periodEndDate as Timestamp
          ).toDate();
          accountResponse.scholarshipStartDate = (
            scholarship.periodStartDate as Timestamp
          ).toDate();
          if (
            (scholarship.periodStartDate as Timestamp).toDate() > new Date()
          ) {
            accountResponse.scholarshipPoints = -100;
          }
        } else {
          accountResponse.scholarshipPoints = -100;
        }
        return Promise.resolve();
      })
      .catch((error) => {
        return Promise.reject(error);
      });

    // Modular: `exists` es MÉTODO (`snap.exists()`), no propiedad — dejar
    // `snap.exists` (la función) sería siempre-truthy y trataría el doc ausente
    // como existente.
    const promotionsPromise = getDoc(doc(_userIncentives, "promotions")).then(
      (snap) => {
        if (snap.exists()) {
          const promotions: any = snap.data();
          // UNA CLAVE NO ES UNA ETIQUETA QUE SE TRADUZCA, Y POR ESO AQUÍ NO HAY
          // UNIÓN — que es justo lo contrario de lo que hace la lectura de la
          // academia y de la licencia, trescientas líneas más arriba en este
          // mismo fichero.
          //
          // Allí se leen las DOS generaciones y manda la de `endDate` mayor,
          // porque son DOS DOCUMENTOS QUE DESCRIBEN LO MISMO: la suscripción de
          // una persona, escrita con el nombre viejo o con el nuevo. Aquí no:
          // cada clave de este libro es un CONTADOR SEPARADO, y sumar dos
          // contadores enseñaría un saldo que la puerta no va a gastar.
          //
          // Lo que se rompía leyendo la clave vieja: un patrocinador con saldo
          // bajo el paquete nuevo veía cero y el botón «Activar SIN Volumen» ni
          // se pintaba; y al revés, quien tenía saldo del paquete viejo veía el
          // botón, lo pulsaba y la puerta lo rechazaba por falta de
          // promociones. Las dos mitades miraban claves distintas.
          //
          // El saldo del paquete viejo se queda INTACTO y sin leer, a propósito
          // (decisión de Hector): quien lo tenga y quiera regalar activaciones,
          // lo pide. Lo que NO puede pasar es enseñarlo aquí como si sirviera.
          //
          // El nombre del campo (`tradingAcademy`) es de esta capa, no un
          // doc-id, y se queda como está: renombrarlo tocaría las pantallas que
          // lo consumen sin arreglar nada.
          accountResponse.promotions.tradingAcademy =
            promotions[CLAVE_PROMO_ACADEMIA] || 0;
          // La licencia NO cambia de clave, y no es un olvido: el backend lee
          // exactamente esta misma cadena sin traducirla (`AdminFuncs.ts`), así
          // que las dos mitades ya coinciden.
          accountResponse.promotions.distributorLicense =
            promotions["distributor-license"] || 0;
          accountResponse.promotions.both = promotions["both"] || 0;
          context.commit(UPDATE_PROMOTIONS, accountResponse.promotions);
        }
      }
    );

    return await Promise.all([
      distributorPromise,
      academyPromise,
      bonusPromise,
      unilevelData,
      affiliatesPromise,
      financialPromise,
      promotionsPromise,
      scholarshipPromise,
    ])
      .then(() => {
        context.commit(INFO_ACCOUNT_SUMMARY, accountResponse);
        return Promise.resolve();
      })
      .catch((error) => {
        return {
          OPCODE: "ERROR",
          message: error.message,
          errorDetail: error,
        };
      });
  },
};

const getters = {
  getAccountSummary(state: AccountSummaryState): AccountSummary {
    return state.account;
  },
  getPromotions(state: AccountSummaryState) {
    return state?.account.promotions;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
